import { io } from "socket.io-client";

let socket = null;

export function getWhiteboardSocket() {
  if (socket) return socket;

  const isBrowser = typeof window !== "undefined";
  const onLocal3000 =
    isBrowser &&
    window.location.hostname === "localhost" &&
    window.location.port === "3000";

  // Local dev uses the separate backend port.
  // Everywhere else we connect back to the current site origin,
  // so production does not depend on switching env values by hand.
  const backendOrigin = onLocal3000 ? "http://localhost:3001" : undefined;

  socket = io(backendOrigin, {
    withCredentials: true,
    transports: ["websocket", "polling"],
  });

  return socket;
}

function emitAck(event, payload = {}, timeoutMs = 8000) {
  const s = getWhiteboardSocket();
  return new Promise((resolve) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ ok: false, error: "ack_timeout" });
    }, Math.max(1200, Number(timeoutMs) || 8000));

    s.emit(event, payload, (res) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(res);
    });
  });
}

export function whiteboardPing(payload = {}) {
  return emitAck("WHITEBOARD:PING", payload);
}

export function whiteboardCreateRoom(payload = {}) {
  return emitAck("WHITEBOARD:CREATE_ROOM", payload);
}

export function whiteboardSetVisibility(code, visibilityType) {
  return emitAck("WHITEBOARD:SET_VISIBILITY", { code, visibilityType });
}

export function whiteboardJoinRoom(code) {
  return emitAck("WHITEBOARD:JOIN_ROOM", { code });
}

export function whiteboardGetRoomState(code) {
  return emitAck("WHITEBOARD:GET_ROOM_STATE", { code });
}

export function whiteboardGetPageWindow(code, pageStart, pageEnd, options = {}) {
  return emitAck("WHITEBOARD:GET_PAGE_WINDOW", {
    code,
    pageStart,
    pageEnd,
    paddingBefore: Number(options?.paddingBefore || 0),
    paddingAfter: Number(options?.paddingAfter || 0),
  });
}

export function whiteboardLeaveRoom() {
  return emitAck("WHITEBOARD:LEAVE_ROOM", {});
}

export function whiteboardUpdateState(code, state, baseVersion, delta = null, options = {}) {
  const preferDeltaOnly = options?.preferDeltaOnly !== false;
  const hasDelta = !!(delta && typeof delta === "object");
  const payload = { code };
  const opId = String(options?.opId || "").trim();
  const clientId = String(options?.clientId || "").trim();
  const timeoutMs = Number(options?.timeoutMs || options?.ackTimeoutMs || 8000);
  if (!hasDelta || !preferDeltaOnly) {
    payload.state = state;
  }
  if (Number.isFinite(Number(baseVersion))) payload.baseVersion = Number(baseVersion);
  if (hasDelta) payload.delta = delta;
  if (opId) payload.opId = opId;
  if (clientId) payload.clientId = clientId;
  return emitAck("WHITEBOARD:UPDATE_STATE", payload, timeoutMs);
}

export function whiteboardSendDraft(code, draft) {
  return emitAck("WHITEBOARD:STROKE_DRAFT", { code, draft });
}

export function whiteboardSendCursor(code, point) {
  const s = getWhiteboardSocket();
  s.emit("WHITEBOARD:CURSOR_MOVE", { code, point });
  return Promise.resolve({ ok: true });
}

export function whiteboardSaveSnapshot(code, title) {
  return emitAck("WHITEBOARD:SAVE_SNAPSHOT", { code, title });
}
